/**
 * VoyageLogView Component
 *
 * Full-screen view of a completed voyage showing the ENTIRE log.
 * Reuses LogEntry component to display every entry chronologically.
 *
 * Phase 3 v2 UPDATE:
 * - Mobile-first design matching new voyage interface
 * - Dark slate header matching MobileVoyageHeader style
 * - Proper scroll handling
 * - Safe area support for iOS
 *
 * FT-196: Voyage Sign-Off
 * - Master can sign off completed voyages
 * - Uses existing SignatureModal component
 */

import React, { useState, useMemo } from 'react';
import { Ship, Calendar, Clock, Users, ArrowLeft, Cloud, Compass, Fuel, Search, X, MapPin, FileText, Radio, ClipboardCheck, Flag, PenTool, Check, Download } from 'lucide-react';
import LogEntry from './ActiveVoyage/LogEntry';
// Pass 2d §1.1 — voyage plan card at top of post-voyage report.
// Card auto-detects read-only mode via voyage.status — see locked decision #1.
import VoyagePlanUnderwayCard from './ActiveVoyage/VoyagePlanUnderwayCard';
// FT-161: Voyage log card components (Phases 1-4)
import {
  PreDepartureCard,
  MilestoneBanner,
  WeatherReportCard,
  OperationCard
} from './VoyageLogCards';
// FT-196: Voyage Sign-Off (Standardisation - Pattern 1: Confirm-only)
import ConfirmSignOffModal from '../../../shared/ConfirmSignOffModal';
import { usePermissions } from '../../../../hooks/usePermissions';
// FT-195: Voyage PDF Export
import { exportVoyagePDF } from '../../../../utils/pdf/voyageExport';
import { portLabel } from '../../../../utils/voyage/portLabel';
// FT-356: register onClose as the shell-level back handler.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const VoyageLogView = ({ voyage, vesselName, onClose, onUpdateVoyage, onLogSignOff }) => {
  useSetBackHandler(onClose);
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // FT-196: Confirm sign-off modal state
  const [showSignOffModal, setShowSignOffModal] = useState(false);

  // FT-196: Permission check for sign-off
  const { can, currentUser } = usePermissions();

  // FT-196: Check if user can sign off voyages (skipper, fleet, or super_admin)
  const canSignOff = can('voyages', 'sign') ||
    ['skipper', 'fleet', 'super_admin'].includes(currentUser?.role);

  // FT-196: Sign-off handler (Pattern 1: Confirm-only, no drawn signature)
  const handleSignOff = async (signOffData) => {
    if (!onUpdateVoyage) {
      console.warn('VoyageLogView: onUpdateVoyage prop not provided');
      setShowSignOffModal(false);
      return;
    }

    // New standardised data structure (no signature image)
    const signOff = {
      confirmedBy: signOffData.confirmedBy,
      role: signOffData.role,
      confirmedAt: signOffData.confirmedAt,
    };

    try {
      await onUpdateVoyage({
        ...voyage,
        signOff
      });

      // FT-196: Log the sign-off to the main logbook
      if (onLogSignOff) {
        const fromName = portLabel(voyage.from, 'Unknown');
        const toName = portLabel(voyage.to, 'Unknown');
        await onLogSignOff({
          voyageId: voyage.id,
          voyageNumber: voyage.voyageNumber,
          route: `${fromName} → ${toName}`,
          signedBy: signOff.confirmedBy,
          signedByRole: signOff.role,
          signedAt: signOff.confirmedAt
        });
      }

      setShowSignOffModal(false);
    } catch (error) {
      console.error('Failed to save sign-off:', error);
    }
  };

  if (!voyage) return null;

  // Get the unified log (the complete chronological record)
  const unifiedLog = voyage.unifiedLog || [];

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDuration = () => {
    if (!voyage.departureTime || !voyage.arrivalTime) return null;
    const start = new Date(voyage.departureTime);
    const end = new Date(voyage.arrivalTime);
    const hours = Math.floor((end - start) / (1000 * 60 * 60));
    const minutes = Math.floor(((end - start) % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Count entries by type for summary
  const entryCounts = {
    checks: unifiedLog.filter(e => e.type === 'check').length,
    positions: unifiedLog.filter(e => e.type === 'position').length,
    weather: unifiedLog.filter(e => e.type === 'weather').length,
    fuel: unifiedLog.filter(e => e.type === 'fuel_reading').length,
    notes: unifiedLog.filter(e => e.type === 'note').length,
    compass: unifiedLog.filter(e => e.type === 'compass_check').length,
    operations: unifiedLog.filter(e => e.type === 'section' && e.operationId).length
  };

  // Type filter options for auditor search
  const typeFilterOptions = [
    { id: 'all', label: 'All', icon: null },
    { id: 'position', label: 'Position', icon: MapPin },
    { id: 'weather', label: 'Weather', icon: Cloud },
    { id: 'fuel_reading', label: 'Fuel', icon: Fuel },
    { id: 'note', label: 'Notes', icon: FileText },
    { id: 'comms', label: 'Comms', icon: Radio },
    { id: 'check', label: 'Checks', icon: ClipboardCheck },
    { id: 'checklist_completion', label: 'Checklists', icon: ClipboardCheck },
    { id: 'section', label: 'Milestones', icon: Flag }
  ];

  // Filter entries based on search and type filter
  const filteredEntries = useMemo(() => {
    let entries = [...unifiedLog];

    // Apply type filter
    if (typeFilter !== 'all') {
      entries = entries.filter(entry => {
        if (typeFilter === 'section') {
          // Milestones are section entries with operationId
          return entry.type === 'section' && entry.operationId;
        }
        return entry.type === typeFilter;
      });
    }

    // Apply text search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      entries = entries.filter(entry => {
        // Search across multiple fields
        const searchableFields = [
          entry.notes,
          entry.text,
          entry.content,
          entry.title,
          entry.latitude,
          entry.longitude,
          entry.position,
          entry.course,
          entry.speed,
          entry.windSpeed,
          entry.windDirection,
          entry.seaState,
          entry.visibility,
          entry.fuelLevel,
          entry.fuelUsed,
          entry.checkType,
          entry.inspector,
          entry.operationType,
          entry.operationName,
          entry.portName,
          entry.message
        ];

        return searchableFields.some(field =>
          field && String(field).toLowerCase().includes(term)
        );
      });
    }

    return entries;
  }, [unifiedLog, typeFilter, searchTerm]);

  // FT-161 Phase 5: Group operations by operationId (same logic as VoyageDocument)
  const operationGroups = useMemo(() => {
    const groups = {};
    unifiedLog.forEach(entry => {
      if (entry.operationId && ['section', 'operation_data', 'check', 'operation_complete'].includes(entry.type)) {
        // Skip pre_departure operations - handled separately by PreDepartureCard
        if (entry.operationType === 'pre_departure' || entry.checkType === 'pre_departure') {
          return;
        }
        if (!groups[entry.operationId]) {
          groups[entry.operationId] = { section: null, data: null, checks: [], complete: null };
        }
        if (entry.type === 'section') groups[entry.operationId].section = entry;
        if (entry.type === 'operation_data') groups[entry.operationId].data = entry;
        if (entry.type === 'check') groups[entry.operationId].checks.push(entry);
        if (entry.type === 'operation_complete') groups[entry.operationId].complete = entry;
      }
    });
    // For completed voyage view, we show ALL operations (complete or not)
    // but prioritize completed ones with their full data
    return groups;
  }, [unifiedLog]);

  // FT-161: Pre-process entries for special rendering
  // Group pre-departure checks, operations, and identify milestones
  const processedEntries = useMemo(() => {
    let entries = [...filteredEntries];

    // Find all pre-departure checks
    const preDepartureChecks = entries.filter(
      e => e.type === 'check' && e.checkType === 'pre_departure'
    );

    // Find LET GO milestone (marks end of pre-departure)
    const letGoIndex = entries.findIndex(
      e => e.type === 'milestone' && e.milestoneType === 'let_go'
    );

    // If we have pre-departure checks and they should be grouped
    if (preDepartureChecks.length > 0 && letGoIndex !== -1) {
      // Remove individual pre-departure checks from entries
      entries = entries.filter(
        e => !(e.type === 'check' && e.checkType === 'pre_departure')
      );

      // Create a grouped entry for pre-departure
      const preDepartureGrouped = {
        id: 'pre_departure_grouped',
        type: 'pre_departure_card',
        timestamp: preDepartureChecks[0]?.timestamp || voyage.departureTime,
        checks: preDepartureChecks
      };

      // Insert grouped card at the beginning
      entries = [preDepartureGrouped, ...entries];
    }

    // FT-161 Phase 5: Replace scattered operation entries with operation_card entries
    const processedOperationIds = new Set();
    const result = [];

    entries.forEach(entry => {
      // Skip operation entries that belong to a grouped operation (not pre_departure)
      if (entry.operationId && operationGroups[entry.operationId]) {
        const group = operationGroups[entry.operationId];
        // Only process each operation once - at the section entry position
        if (entry.type === 'section' && !processedOperationIds.has(entry.operationId)) {
          processedOperationIds.add(entry.operationId);
          // Create an operation_card entry
          result.push({
            id: `operation_card_${entry.operationId}`,
            type: 'operation_card',
            operationId: entry.operationId,
            operationType: entry.operationType,
            timestamp: entry.timestamp,
            sectionEntry: group.section,
            operationDataEntry: group.data,
            checks: group.checks,
            completeEntry: group.complete
          });
        }
        // Skip other operation-related entries (operation_data, checks, operation_complete)
        return;
      }
      // Keep all other entries as-is
      result.push(entry);
    });

    return result;
  }, [filteredEntries, voyage.departureTime, operationGroups]);

  // Calculate POB
  const pob = (voyage.crewCount || 0) + (voyage.passengerCount || 0);

  // Get from/to names
  const fromName = portLabel(voyage.from, 'Unknown');
  const toName = portLabel(voyage.to, 'Unknown');

  // FT-222: Purpose display (pattern from VoyageHeader.js)
  const purposeMap = {
    'crew_transfer': 'Crew Transfer',
    'survey': 'Survey',
    'passenger_run': 'Passenger Run',
    'cargo': 'Cargo',
    'training': 'Training',
    'other': 'Other'
  };
  const purposeDisplay = voyage.purpose === 'other' && voyage.purposeOther
    ? voyage.purposeOther
    : purposeMap[voyage.purpose] || voyage.purpose || null;

  // FT-222: Master name (pattern from VoyageHeader.js line 321)
  const masterName = voyage.masterDesignation?.masterName || voyage.master || null;

  // FT-222: Crew names (snapshotted at voyage creation)
  const crewNamesDisplay = voyage.crewNames?.length > 0
    ? voyage.crewNames.join(', ')
    : null;

  return (
    <div className="flex flex-col min-h-full">
      {/* Dark Header - Compact, sticky */}
      <div className="bg-gray-900 text-white sticky top-0 z-10 safe-area-top">
        <div className="px-5 py-4">
          {/* Top row - Back button and duration */}
          <div className="flex justify-between items-center mb-3">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="text-sm font-medium">Back</span>
            </button>

            {getDuration() && (
              <div className="text-sm text-slate-300 font-mono bg-slate-800/80 px-2 py-1 rounded">
                {getDuration()}
              </div>
            )}
          </div>

          {/* Voyage title and route */}
          <div className="mb-2">
            <h2 className="text-xl font-bold tracking-tight">
              {voyage.voyageNumber || 'Voyage Log'}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {fromName} → {toName}
            </p>
          </div>

          {/* Date row */}
          <div className="flex items-center gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {formatDate(voyage.departureTime)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {formatTime(voyage.departureTime)} - {formatTime(voyage.arrivalTime) || 'In Progress'}
            </span>
          </div>
        </div>
      </div>

      {/* Log Content - Scrollable */}
      <div className="flex-1 p-4 pb-24">
        {/* Pass 2d §1.2 — Voyage plan card at top of report (read-only mode).
            Card auto-detects voyage.status === 'completed' via its internal
            isReadOnly derivation at VoyagePlanUnderwayCard.js:630-635. NO
            handler props are passed — onUpdatePlanItem MUST be undefined to
            keep the belt-and-braces write-back effect at :779-797 inert
            against a frozen voyage record. key={voyage.id} ensures clean
            React reconciliation if the modal is reopened for a different
            voyage (vessel-switcher safety). */}
        <VoyagePlanUnderwayCard
          key={voyage.id}
          voyage={voyage}
        />

        {/* Stats Strip - Scrolls with content */}
        <div className="bg-white rounded-xl px-3 py-3 border border-gray-200 shadow-sm mb-3">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-lg font-bold text-gray-900">{entryCounts.positions}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Positions</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{entryCounts.operations}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Operations</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{entryCounts.notes}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Notes</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{pob || '--'}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">POB</p>
            </div>
          </div>
        </div>

        {/* Search & Filter Bar - For Auditor Review */}
        {unifiedLog.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-4">
            {/* Search Input */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search log entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Type Filter Tabs */}
            <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
              {typeFilterOptions.map((option) => {
                const Icon = option.icon;
                const isActive = typeFilter === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => setTypeFilter(option.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-cyan-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {Icon && <Icon className="h-3.5 w-3.5" />}
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* Results Count */}
            {(searchTerm || typeFilter !== 'all') && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Showing {filteredEntries.length} of {unifiedLog.length} entries
                </p>
                {(searchTerm || typeFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setTypeFilter('all');
                    }}
                    className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Quick Summary Cards - Only show if data exists */}
        {(entryCounts.weather > 0 || entryCounts.fuel > 0 || entryCounts.compass > 0) && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {entryCounts.weather > 0 && (
              <div className="bg-cyan-50 rounded-lg px-3 py-2 flex items-center gap-2 shrink-0">
                <Cloud className="h-4 w-4 text-cyan-600" />
                <span className="text-xs font-medium text-cyan-700">{entryCounts.weather} weather</span>
              </div>
            )}
            {entryCounts.fuel > 0 && (
              <div className="bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-2 shrink-0">
                <Fuel className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-medium text-amber-700">{entryCounts.fuel} fuel</span>
              </div>
            )}
            {entryCounts.compass > 0 && (
              <div className="bg-purple-50 rounded-lg px-3 py-2 flex items-center gap-2 shrink-0">
                <Compass className="h-4 w-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-700">{entryCounts.compass} compass</span>
              </div>
            )}
          </div>
        )}

        {/* THE LOG - Full Chronological View */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Log Header - Light style for report view */}
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <Ship className="h-4 w-4 text-gray-500" />
              Voyage Log ({searchTerm || typeFilter !== 'all' ? `${processedEntries.length} of ${unifiedLog.length}` : unifiedLog.length} entries)
            </h3>
          </div>

          {/* Log Entries - FT-161: Use new card components for pre-departure, weather, operations, and milestones */}
          <div className="divide-y divide-gray-100">
            {processedEntries.length > 0 ? (
              processedEntries.map((entry, index) => {
                // FT-161 Phase 1: Render Pre-Departure Card
                if (entry.type === 'pre_departure_card') {
                  return (
                    <div key={entry.id} className="px-3 py-1.5">
                      <PreDepartureCard
                        voyage={voyage}
                        checks={entry.checks}
                        timestamp={entry.timestamp}
                      />
                    </div>
                  );
                }

                // FT-161 Phase 1: Render Milestone Banners (LET GO / ALL FAST)
                if (entry.type === 'milestone') {
                  return (
                    <div key={entry.id || index} className="px-3 py-1.5">
                      <MilestoneBanner entry={entry} />
                    </div>
                  );
                }

                // FT-161 Phase 3: Render Weather Report Card (card has mx-4 my-2, reduce to my-1)
                if (entry.type === 'weather') {
                  return (
                    <div key={entry.id || index} className="py-0.5">
                      <WeatherReportCard entry={entry} />
                    </div>
                  );
                }

                // FT-161 Phase 4/5: Render Operation Card for grouped operations (card has mx-4 my-2, reduce to my-1)
                if (entry.type === 'operation_card') {
                  return (
                    <div key={entry.id || `operation-${entry.operationId}`} className="py-0.5">
                      <OperationCard
                        operationType={entry.operationType}
                        sectionEntry={entry.sectionEntry}
                        operationDataEntry={entry.operationDataEntry}
                        checks={entry.checks}
                        completeEntry={entry.completeEntry}
                      />
                    </div>
                  );
                }

                // Default: Use existing LogEntry for all other types
                return (
                  <LogEntry
                    key={entry.id || index}
                    entry={entry}
                    voyage={voyage}
                    isEditable={false}
                    canModify={false}
                  />
                );
              })
            ) : unifiedLog.length > 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No entries match your search</p>
                <p className="text-xs text-gray-400 mt-1">Try a different search term or filter</p>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Ship className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No log entries recorded</p>
                <p className="text-xs text-gray-400 mt-1">This voyage has no logged events</p>
              </div>
            )}
          </div>
        </div>

        {/* Voyage Details Card */}
        <div className="mt-4 bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Voyage Details</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 block text-xs">From</span>
              <p className="font-medium text-gray-900">{fromName}</p>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">To</span>
              <p className="font-medium text-gray-900">{toName}</p>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">Departed</span>
              <p className="font-medium text-gray-900">{formatTime(voyage.departureTime) || 'N/A'}</p>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">Arrived</span>
              <p className="font-medium text-gray-900">{formatTime(voyage.arrivalTime) || 'In Progress'}</p>
            </div>
            {/* FT-222: Purpose */}
            {purposeDisplay && (
              <div>
                <span className="text-gray-500 block text-xs">Purpose</span>
                <p className="font-medium text-gray-900">{purposeDisplay}</p>
              </div>
            )}
            {/* FT-222: Master */}
            {masterName && (
              <div>
                <span className="text-gray-500 block text-xs">Master</span>
                <p className="font-medium text-gray-900">{masterName}</p>
              </div>
            )}
            {/* FT-222: Crew names (with fallback to count) */}
            {voyage.crewCount > 0 && (
              <div>
                <span className="text-gray-500 block text-xs">Crew</span>
                <p className="font-medium text-gray-900">
                  {crewNamesDisplay || voyage.crewCount}
                </p>
              </div>
            )}
            {voyage.passengerCount > 0 && (
              <div>
                <span className="text-gray-500 block text-xs">Passengers</span>
                <p className="font-medium text-gray-900">{voyage.passengerCount}</p>
              </div>
            )}
          </div>
        </div>

        {/* FT-196: Voyage Sign-Off Card */}
        {voyage.status === 'completed' && (
          <div className="mt-4 bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <PenTool className="h-4 w-4 text-gray-500" />
              Voyage Sign-Off
            </h4>

            {voyage.signOff ? (
              // Already signed - show confirmation (FT-196: Pattern 1 - no signature image)
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-800">
                      Signed off by {voyage.signOff.confirmedBy || voyage.signOff.signedBy?.name || (typeof voyage.signOff.signedBy === 'string' ? voyage.signOff.signedBy : 'Unknown')}
                    </p>
                    <p className="text-xs text-green-700 mt-0.5">
                      {voyage.signOff.role || voyage.signOff.signedBy?.role || voyage.signOff.signedByRole}
                    </p>
                    {(voyage.signOff.confirmedAt || voyage.signOff.timestamp || voyage.signOff.signedAt) && (
                      <p className="text-xs text-green-600 mt-1">
                        {new Date(voyage.signOff.confirmedAt || voyage.signOff.timestamp || voyage.signOff.signedAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : canSignOff ? (
              // Not signed + user can sign - show button
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  This voyage has not been signed off.
                </p>
                <button
                  onClick={() => setShowSignOffModal(true)}
                  className="w-full py-3 bg-cyan-600 text-white rounded-xl font-medium hover:bg-cyan-700 transition-colors flex items-center justify-center gap-2"
                >
                  <PenTool className="h-4 w-4" />
                  Sign Off Voyage
                </button>
              </div>
            ) : (
              // Not signed + user cannot sign - show info
              <div>
                <p className="text-sm text-gray-500">
                  This voyage has not been signed off. Only the Master can sign off voyages.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Action Bar - Fixed */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-20 safe-area-bottom">
        <div className="px-4 py-3 max-w-md mx-auto flex gap-3">
          {/* FT-195: Download PDF button - only show for completed voyages */}
          {voyage.status === 'completed' && (
            <button
              onClick={() => exportVoyagePDF(voyage, { vesselName: vesselName || 'Unknown Vessel' })}
              className="flex-1 py-3 bg-cyan-600 text-white rounded-xl font-medium hover:bg-cyan-700 transition-colors flex items-center justify-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </button>
          )}
          <button
            onClick={onClose}
            className={`${voyage.status === 'completed' ? 'flex-1' : 'w-full'} py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors`}
          >
            Close
          </button>
        </div>
      </div>

      {/* FT-196: Confirm Sign-Off Modal (Pattern 1: no drawn signature) */}
      <ConfirmSignOffModal
        isOpen={showSignOffModal}
        onClose={() => setShowSignOffModal(false)}
        onConfirm={handleSignOff}
        title={`Sign Off ${voyage.voyageNumber || 'Voyage'}`}
        description={`You are confirming that voyage ${voyage.voyageNumber || ''} from ${fromName} to ${toName} has been reviewed and approved.`}
      />

      {/* Safe area styles */}
      <style>{`
        .safe-area-top {
          padding-top: env(safe-area-inset-top, 0);
        }
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </div>
  );
};

export default VoyageLogView;
